import React, { useState } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthProvider";
import { subscribeEmail } from "../service/auth";
import { handleAuthSuccessRedirect } from "../utils/authRedirect";
import "./GoogleOAuthButton.css";

function GoogleOAuthButton() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { handleSuccessfulSignup } = useAuth();

  const login = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setIsLoading(true);
      setError(null);

      try {
        // Fetch user info from Google
        const userInfoResponse = await fetch(
          "https://www.googleapis.com/oauth2/v3/userinfo",
          {
            headers: {
              Authorization: `Bearer ${tokenResponse.access_token}`,
            },
          }
        );

        if (!userInfoResponse.ok) {
          throw new Error("Failed to fetch user info from Google");
        }

        const userInfo = await userInfoResponse.json();
        const email = userInfo.email;

        if (!email) {
          throw new Error("No email found in Google account");
        }

        // Sign up with email from Google OAuth
        const signupResponse = await subscribeEmail({
          email,
          newsletter_on: true,
          zebra_id: "PartyParty-Free-Trial-Mar26",
        });

        // Store auth token and user data
        if (signupResponse.data && signupResponse.data.token) {
          // Update auth state immediately
          handleSuccessfulSignup(signupResponse.data.token, signupResponse.data.data);

          // Mark that account was created
          localStorage.setItem("hasCreatedAccount", "true");

          handleAuthSuccessRedirect(navigate, location);
        } else {
          throw new Error("Invalid response from signup API");
        }
      } catch (err) {
        console.error("Google OAuth error:", err);
        setError(
          err.message || "Failed to sign in with Google. Please try again."
        );
      } finally {
        setIsLoading(false);
      }
    },
    onError: (error) => {
      console.error("Google OAuth error:", error);
      setError("Failed to sign in with Google. Please try again.");
    },
  });

  return (
    <div className="google-oauth-container">
      <button
        className="google-oauth-button"
        onClick={() => login()}
        disabled={isLoading}
      >
        <svg className="google-icon" viewBox="0 0 24 24" width="24" height="24">
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
        {isLoading ? "Signing in..." : "Continue with Google"}
      </button>
      {error && <div className="google-oauth-error">{error}</div>}
    </div>
  );
}

export default GoogleOAuthButton;
